/**
 * The one stock figure under a picker cell, and the one short rule (D-5).
 *
 * Built on the BLU-1458 browser rule (`withBackup` / `coveredByBackup`), never
 * a second rule:
 * - an own SKU counts own stock plus the 3PL's back-up behind it, orange when
 *   back-up alone covers it;
 * - a supply alias (a client SKU on the 3PL's blank) counts the 3PL's pool
 *   only: the alias holds no stock of its own, so own stock never counts
 *   (the supply tier of the server's stock rule).
 *
 * Pure: no React, no I/O. Plan 104.1-05 points `shortBy` in build-payload at
 * `shortFor`, so the grid and the rail checks read one rule.
 */
import { coveredByBackup, withBackup } from '@/features/inventory/utils/stockUtils';
import { parseQuantity } from './build-payload';

export interface StockCellInput {
  /** Own available stock at the floor (may be below 0). */
  available: number;
  /** Back-up behind an own SKU, or the 3PL's pool on a supply alias. */
  backup: number;
  supplyAlias: boolean;
}

export interface StockFigure {
  /** The figure shown: own plus back-up, or the pool on a supply alias. */
  total: number;
  /** Back-up alone covers it (orange, BLU-1458). */
  coveredByBackup: boolean;
  /** The figure is the 3PL's pool (a supply alias). */
  fromPool: boolean;
}

export function stockFigure(cell: StockCellInput): StockFigure {
  if (cell.supplyAlias) {
    return { total: Math.max(0, cell.backup), coveredByBackup: false, fromPool: true };
  }
  return {
    total: withBackup(cell.available, cell.backup),
    coveredByBackup: coveredByBackup(cell.available, cell.backup),
    fromPool: false,
  };
}

/** Pieces the typed quantity is over the figure (0 when covered); a figure below 0 counts as 0 stock. */
export function shortFor(quantityText: string, figure: StockFigure): number {
  return Math.max(0, parseQuantity(quantityText) - Math.max(0, figure.total));
}

/** The own substitute a size may carry (owner-direct orders only; see types StockLineSubstitute). */
export interface SubstituteHintInput {
  sku: string;
  available: number;
  justInTime: boolean;
}

/** The substitute can supply `pieces` (just in time is unlimited). */
export function substituteCovers(substitute: SubstituteHintInput, pieces: number): boolean {
  return substitute.justInTime || substitute.available >= Math.max(1, pieces);
}

/**
 * The picker's suggestion for a size with nothing available (figure 0 or
 * below): its own substitute, only when that substitute covers the typed
 * quantity (at least 1; just in time always), the oversellSuggestion rule.
 * Null without a substitute, or when the size has stock.
 */
export function replaceSuggestion(
  cell: StockCellInput & { quantity?: string; substitute?: SubstituteHintInput },
): SubstituteHintInput | null {
  if (!cell.substitute || stockFigure(cell).total > 0) return null;
  return substituteCovers(cell.substitute, parseQuantity(cell.quantity ?? '')) ? cell.substitute : null;
}

/**
 * The oversell warning for a typed quantity: how many pieces over the
 * figure, and the substitute to offer instead, only when it covers the WHOLE
 * quantity (a substitute that would oversell too is not a fix).
 */
export function oversellSuggestion(
  cell: StockCellInput & { quantity: string; substitute?: SubstituteHintInput },
): { short: number; substitute: SubstituteHintInput | null } {
  const short = shortFor(cell.quantity, stockFigure(cell));
  if (short === 0) return { short, substitute: null };
  const sub = cell.substitute && substituteCovers(cell.substitute, parseQuantity(cell.quantity)) ? cell.substitute : null;
  return { short, substitute: sub };
}
